import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

// Removed useQuery

const AdminEmail = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;

  // Admin verification
  useEffect(() => {
    if (!user || user['user_type'] !== 2) {
      navigate('/dashboard', { replace: true });
    }
    // eslint-disable-next-line
  }, [user]);

  const [editUser, setEditUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState(1);
  const [credentialVisibility, setCredentialVisibility] = useState(false);
  const [success, setSuccess] = useState('');
  const [domains, setDomains] = useState([]);
  const [domainName, setDomainName] = useState('');
  const [domainFrequency, setDomainFrequency] = useState(24);
  const [domainType, setDomainType] = useState(1);
  const [domainError, setDomainError] = useState('');
  const [domainSuccess, setDomainSuccess] = useState('');
  
  // URL management state
  const [urls, setUrls] = useState([]);
  const [urlName, setUrlName] = useState('');
  const [urlFrequency, setUrlFrequency] = useState(24);
  const [urlType, setUrlType] = useState(1);
  const [urlError, setUrlError] = useState('');
  const [urlSuccess, setUrlSuccess] = useState('');

  // Slug management state
  const [slugs, setSlugs] = useState([]);
  const [slugName, setSlugName] = useState('');
  const [slugError, setSlugError] = useState('');
  const [slugSuccess, setSlugSuccess] = useState('');

  // CSV import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    if (!userId) {
      navigate('/admin');
      return;
    }
    fetchUser();
    fetchDomains();
    fetchUrls();
    fetchSlugs();
    // eslint-disable-next-line
  }, [userId]);

  const fetchUser = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all users and find the one with userId
      const data = await api.getAllUsers();
      const found = data.users.find(u => String(u['id']) === String(userId));
      if (!found) {
        setError('User not found. It may have been deleted.');
        setTimeout(() => {
          navigate('/admin'); // Redirect back to admin page after a short delay
        }, 2000);
        setLoading(false);
        return;
      }
      setEditUser(found);
      setUserType(found['user_type']);
      setCredentialVisibility(found['credential_visibility'] || false);
    } catch (err) {
      console.error('Fetch user error:', err);
      setError(`Failed to load user data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    setDomainError('');
    try {
      const data = await api.getDomainsByUser(userId);
      setDomains(data.domains || []);
    } catch (err) {
      setDomainError(err.message);
    }
  };

  const fetchUrls = async () => {
    setUrlError('');
    try {
      const data = await api.getUrlsByUser(userId);
      setUrls(data.urls || []);
    } catch (err) {
      setUrlError(err.message);
    }
  };

  const fetchSlugs = async () => {
    setSlugError('');
    try {
      const data = await api.getSlugsByUser(userId);
      setSlugs(data.slugs || []);
    } catch (err) {
      setSlugError(err.message);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess('');
    setLoading(true);
    
    try {
      await api.updateUser(userId, password, confirmPassword, userType);
      setSuccess('User updated successfully');
      setPassword('');
      setConfirmPassword('');
      fetchUser();
    } catch (err) {
      console.error('Update user error:', err);
      if (err.message.includes('User not found')) {
        setError('User not found. It may have been deleted.');
        navigate('/admin'); // Redirect back to admin page
      } else if (err.message.includes('Passwords do not match')) {
        setError('Passwords do not match. Please check your password confirmation.');
      } else if (err.message.includes('Password must be at least')) {
        setError('Password does not meet requirements. Please ensure it has at least 8 characters with uppercase, lowercase, number, and special character (e.g. !@#$%^&*.).');
      } else if (err.message.includes('Failed to update user')) {
        setError('Failed to update user. Please try again or contact support if the problem persists.');
      } else {
        setError(`Failed to update user: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialVisibilityToggle = async () => {
    if (userType === 2) return; 
    setError(null);
    setSuccess('');
    setLoading(true);
    
    try {
      const newValue = !credentialVisibility;
      await api.updateUserCredentialVisibility(userId, newValue);
      setCredentialVisibility(newValue);
      setSuccess('Credential visibility updated successfully');
    } catch (err) {
      console.error('Update credential visibility error:', err);
      if (err.message.includes('User not found')) {
        setError('User not found. It may have been deleted.');
        setTimeout(() => {
          navigate('/admin'); // Redirect back to admin page
        }, 2000);
      } else if (err.message.includes('Failed to update credential visibility')) {
        setError('Failed to update credential visibility. Please try again.');
      } else {
        setError(`Failed to update credential visibility: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setDomainError('');
    setDomainSuccess('');
    try {
      // Convert monthly frequency (-2) to backend format (frequency=0, week_day=0)
      const frequency = domainFrequency === -2 ? 0 : domainFrequency;
      const searchType = domainFrequency === -2 ? "monthly" : "weekly";
      await api.createDomainForUser(userId, domainName, frequency, domainType, searchType);
      setDomainSuccess('Domain added successfully');
      setDomainName('');
      setDomainFrequency(24);
      setDomainType(1);
      fetchDomains();
    } catch (err) {
      setDomainError(err.message);
    }
  };

  const handleDeleteDomain = async (domain) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    setDomainError('');
    try {
      await api.deleteDomainForUser(userId, domain);
      fetchDomains();
    } catch (err) {
      setDomainError(err.message);
    }
  };

  const handleAddUrl = async (e) => {
    e.preventDefault();
    setUrlError('');
    setUrlSuccess('');
    try {
      // Convert monthly frequency (-2) to backend format (frequency=0, week_day=0)
      const frequency = urlFrequency === -2 ? 0 : urlFrequency;
      const searchType = urlFrequency === -2 ? "monthly" : "weekly";
      await api.createUrlForUser(userId, urlName, frequency, urlType, searchType);
      setUrlSuccess('URL added successfully');
      setUrlName('');
      setUrlFrequency(24);
      setUrlType(1);
      fetchUrls();
    } catch (err) {
      setUrlError(err.message);
    }
  };

  const handleDeleteUrl = async (url) => {
    if (!window.confirm('Are you sure you want to delete this URL?')) return;
    setUrlError('');
    try {
      await api.deleteUrlForUser(userId, url);
      fetchUrls();
    } catch (err) {
      setUrlError(err.message);
    }
  };

  const handleAddSlug = async (e) => {
    e.preventDefault();
    setSlugError('');
    setSlugSuccess('');
    try {
      await api.createSlugForUser(userId, slugName);
      setSlugSuccess('Slug added successfully');
      setSlugName('');
      fetchSlugs();
    } catch (err) {
      setSlugError(err.message);
    }
  };

  const handleDeleteSlug = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this slug?')) return;
    setSlugError('');
    try {
      await api.deleteSlugForUser(userId, slug);
      fetchSlugs();
    } catch (err) {
      setSlugError(err.message);
    }
  };

  const handleDownloadTemplate = async () => {
    setImportError('');
    try {
      const blob = await api.downloadImportTemplate();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'osiris-import-template.csv';
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    } catch (err) {
      setImportError(err.message);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    // Lets the same file be picked again after fixing it
    e.target.value = '';
    if (!file) return;
    setImportError('');
    setImportResult(null);
    setImporting(true);
    try {
      const result = await api.importForUser(userId, file);
      setImportResult(result);
      fetchDomains();
      fetchUrls();
      fetchSlugs();
    } catch (err) {
      setImportError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    setError(null);
    try {
      await api.deleteUser(userId);
      navigate('/admin');
    } catch (err) {
      setError(err.message);
    }
  };

  // Separate domains by type
  const primaryDomains = domains.filter(d => d['domain_type'] === 1); // domain_type
  const thirdPartyDomains = domains.filter(d => d['domain_type'] === 2);

  // Separate URLs by type
  const employeeUrls = urls.filter(url => url['url_type'] & 1);
  const userUrls = urls.filter(url => url['url_type'] & 2);

  // Helper function to format frequency display
  const formatFrequency = (frequency, weekDay) => {
    if (frequency === -1) {
      return 'Never';
    } else if (frequency === 0) {
      return weekDay === 0 ? 'Monthly' : 'Weekly';
    } else {
      return `${frequency}x`;
    }
  };

  if (loading) return <div className="container"><Header /><p>Loading user...</p></div>;
  if (error) return <div className="container"><Header /><p className="error-message">{error}</p></div>;
  if (!editUser) return null;

  return (
    <>
      <Header />
      <div className="container" style={{ maxWidth: 1100, marginTop: 40, padding: '0 clamp(16px, 3vw, 24px)' }}>
        <div style={{ display: 'flex', gap: 'clamp(20px, 4vw, 40px)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Left: User Update Card */}
          <div style={{ flex: 1, minWidth: 320, background: 'var(--white)', borderRadius: 12, boxShadow: '0 2px 12px rgba(30,39,56,0.09)', padding: 'clamp(20px, 3vw, 32px)', marginBottom: 24, position: 'relative' }}>
            <button
              onClick={handleDelete}
              className="btn btn-link"
              style={{
                color: 'var(--error)',
                fontSize: 'clamp(16px, 2.5vw, 22px)',
                padding: 'clamp(6px, 1vw, 8px)',
                display: 'flex',
                alignItems: 'center',
                position: 'absolute',
                top: 'clamp(16px, 2vw, 24px)',
                right: 'clamp(16px, 2vw, 24px)',
                zIndex: 10,
                minWidth: 'fit-content'
              }}
              title="Delete User"
            >
              <i className="fas fa-trash-alt"></i>
            </button>
            <div style={{ marginBottom: 24, paddingRight: 'clamp(40px, 6vw, 60px)' }}>
              <h2 className="mb-0" style={{ 
                fontWeight: 600, 
                fontSize: 'clamp(0.8rem, 2.5vw, 1.5rem)', 
                color: 'var(--text)',
                marginBottom: 8
              }}>
                Edit User:
              </h2>
              <div style={{ 
                fontSize: 'clamp(0.9rem, 2.2vw, 1.3rem)', 
                color: 'var(--text)',
                fontWeight: 500,
                wordBreak: 'break-all',
                overflowWrap: 'break-word'
              }}>
                {editUser['email']}
              </div>
            </div>
            {success && <div className="success-message mb-3">{success}</div>}
            {error && <div className="error-message mb-3">{error}</div>}
            <form onSubmit={handleUpdate} className="mb-4">
              <div className="form-group mb-3">
                <label htmlFor="password" className="mb-1">New Password</label>
                <input
                  id="password"
                  className="form-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group mb-3">
                <label htmlFor="confirmPassword" className="mb-1">Confirm Password</label>
                <input
                  id="confirmPassword"
                  className="form-input"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
              </div>
              <div className="form-group mb-4">
                <label htmlFor="userType" className="mb-1">Role</label>
                <select
                  id="userType"
                  className="form-input"
                  value={userType}
                  onChange={e => setUserType(parseInt(e.target.value))}
                >
                  <option value={1}>User</option>
                  <option value={2}>Admin</option>
                </select>
              </div>
              <div className="form-group mb-4">
                <label className="mb-1">Credential Visibility</label>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12,
                  padding: '8px 0'
                }}>
                  <button
                    type="button"
                    onClick={handleCredentialVisibilityToggle}
                    disabled={userType === 2}
                    style={{
                      width: 48,
                      height: 24,
                      borderRadius: 12,
                      border: 'none',
                      background: userType === 2 || credentialVisibility ? 'var(--primary-color)' : '#ccc',
                      cursor: userType === 2 ? 'not-allowed' : 'pointer',
                      position: 'relative',
                      transition: 'background 0.2s',
                      opacity: userType === 2 ? 0.6 : 1
                    }}
                    title={userType === 2 ? 'Admin users always have credential visibility enabled' : 'Toggle credential visibility'}
                  >
                    <div style={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      background: 'white',
                      position: 'absolute',
                      top: 3,
                      left: userType === 2 || credentialVisibility ? 27 : 3,
                      transition: 'left 0.2s',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </button>
                  <span style={{ 
                    fontSize: '0.9rem', 
                    color: userType === 2 ? 'var(--text-light)' : 'var(--text)',
                    fontWeight: 500
                  }}>
                    {userType === 2 ? 'Always enabled for admins' : (credentialVisibility ? 'Enabled' : 'Disabled')}
                  </span>
                </div>
              </div>
              <div className="form-footer" style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn-primary"
                  style={{
                    width: '100%',
                    background: 'var(--primary-color)',
                    color: 'var(--secondary-color)',
                    transition: 'background 0.18s',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0c000'}
                  onMouseOut={e => e.currentTarget.style.background = 'var(--primary-color)'}
                >
                  Update User
                </button>
              </div>
            </form>
          </div>

          {/* Right: Domain and URL Management Card */}
          <div style={{ flex: 1.2, minWidth: 340, background: 'var(--white)', borderRadius: 12, boxShadow: '0 2px 12px rgba(30,39,56,0.09)', padding: 'clamp(20px, 3vw, 32px)', marginBottom: 24 }}>
            {/* Domains Section */}
            <h3 style={{ fontWeight: 600, fontSize: '1.2rem', color: 'var(--text)', marginBottom: 20 }}>Monitored Domains</h3>
            {domainSuccess && <div className="success-message mb-2">{domainSuccess}</div>}
            {domainError && <div className="error-message mb-2">{domainError}</div>}
            <form onSubmit={handleAddDomain}
              style={{
                display: 'flex',
                gap: 6,
                marginBottom: 14,
                flexWrap: 'wrap',
                alignItems: 'center',
                background: 'none', // removed gray background
                borderRadius: 6,
                padding: '2px 3x',
                boxShadow: '0 1px 3px rgba(30,39,56,0.04)',
                minHeight: 0
              }}>
              <input
                type="text"
                className="form-input"
                placeholder="Domain name"
                value={domainName}
                onChange={e => setDomainName(e.target.value)}
                style={{ flex: 2, minWidth: 80, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
                required
              />
              <select
                className="form-input"
                value={domainFrequency}
                onChange={e => setDomainFrequency(parseInt(e.target.value))}
                style={{ flex: 1, minWidth: 50, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
              >
                <option value={-1}>Never</option>
                <option value={-2}>Monthly</option>
                <option value={0}>Weekly</option>
                {[1,2,3,4,6,8,12,24].map(f => <option key={f} value={f}>{f}x</option>)}
              </select>
              <select
                className="form-input"
                value={domainType}
                onChange={e => setDomainType(parseInt(e.target.value))}
                style={{ flex: 1, minWidth: 70, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
              >
                <option value={1}>Primary</option>
                <option value={2}>Third-party</option>
              </select>
              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  flex: 1,
                  minWidth: 60,
                  fontSize: 13,
                  padding: '3px 0',
                  height: 28,
                  borderRadius: 4,
                  boxShadow: 'none',
                  fontWeight: 500,
                  background: 'var(--primary-color)',
                  color: 'var(--secondary-color)',
                  transition: 'background 0.18s',
                  border: 'none',
                  marginLeft: 2
                }}
                onMouseOver={e => e.currentTarget.style.background = '#f0c000'}
                onMouseOut={e => e.currentTarget.style.background = 'var(--primary-color)'}
              >
                Add
              </button>
            </form>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 32 }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <h4 style={{ fontWeight: 500, fontSize: '1rem', marginBottom: 8, color: 'var(--text)' }}>Primary Domains</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {primaryDomains.length === 0 && <li style={{ color: 'var(--text-light)' }}>None</li>}
                  {primaryDomains.map(d => (
                    <li key={d['id']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '4px 0' }}>
                      <div style={{ 
                        flex: 1, 
                        minWidth: 0, 
                        display: 'flex', 
                        alignItems: 'center',
                        overflow: 'hidden'
                      }}>
                        <span style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0
                        }} title={`@${String(d['name'] || '').replace(/^@+/, '')}`}>
                          @{String(d['name'] || '').replace(/^@+/, '')}
                        </span>
                        <span style={{ 
                          fontSize: 12, 
                          color: 'var(--text-light)', 
                          marginLeft: 8,
                          flexShrink: 0
                        }} title="Frequency">
                          ({formatFrequency(d['frequency'], d['week_day'])})
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteDomain(d['name'])}
                        className="btn btn-link"
                        style={{ color: 'var(--error)', fontSize: 16, marginLeft: 8, flexShrink: 0 }}
                        title="Delete Domain"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <h4 style={{ fontWeight: 500, fontSize: '1rem', marginBottom: 8, color: 'var(--text)' }}>Third-party Domains</h4>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {thirdPartyDomains.length === 0 && <li style={{ color: 'var(--text-light)' }}>None</li>}
                  {thirdPartyDomains.map(d => (
                    <li key={d['id']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '4px 0' }}>
                      <div style={{ 
                        flex: 1, 
                        minWidth: 0, 
                        display: 'flex', 
                        alignItems: 'center',
                        overflow: 'hidden'
                      }}>
                        <span style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1,
                          minWidth: 0
                        }} title={`@${String(d['name'] || '').replace(/^@+/, '')}`}>
                          @{String(d['name'] || '').replace(/^@+/, '')}
                        </span>
                        <span style={{ 
                          fontSize: 12, 
                          color: 'var(--text-light)', 
                          marginLeft: 8,
                          flexShrink: 0
                        }} title="Frequency">
                          ({formatFrequency(d['frequency'], d['week_day'])})
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteDomain(d['name'])}
                        className="btn btn-link"
                        style={{ color: 'var(--error)', fontSize: 16, marginLeft: 8, flexShrink: 0 }}
                        title="Delete Domain"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24 }}>
              <h3 style={{ fontWeight: 600, fontSize: '1.2rem', color: 'var(--text)', marginBottom: 20 }}>Monitored URLs</h3>
              {urlSuccess && <div className="success-message mb-2">{urlSuccess}</div>}
              {urlError && <div className="error-message mb-2">{urlError}</div>}
              <form onSubmit={handleAddUrl}
                style={{
                  display: 'flex',
                  gap: 6,
                  marginBottom: 14,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  background: 'none',
                  borderRadius: 6,
                  padding: '2px 3x',
                  boxShadow: '0 1px 3px rgba(30,39,56,0.04)',
                  minHeight: 0
                }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="URL"
                  value={urlName}
                  onChange={e => setUrlName(e.target.value)}
                  style={{ flex: 2, minWidth: 80, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
                  required
                />
                  <select
                    className="form-input"
                    value={urlFrequency}
                    onChange={e => setUrlFrequency(parseInt(e.target.value))}
                    style={{ flex: 1, minWidth: 50, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
                  >
                    <option value={-1}>Never</option>
                    <option value={-2}>Monthly</option>
                    <option value={0}>Weekly</option>
                    {[1,2,3,4,6,8,12,24].map(f => <option key={f} value={f}>{f}x</option>)}
                  </select>
                <select
                  className="form-input"
                  value={urlType}
                  onChange={e => setUrlType(parseInt(e.target.value))}
                  style={{ flex: 1, minWidth: 70, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
                >
                  <option value={1}>Employee</option>
                  <option value={2}>User</option>
                </select>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    flex: 1,
                    minWidth: 60,
                    fontSize: 13,
                    padding: '3px 0',
                    height: 28,
                    borderRadius: 4,
                    boxShadow: 'none',
                    fontWeight: 500,
                    background: 'var(--primary-color)',
                    color: 'var(--secondary-color)',
                    transition: 'background 0.18s',
                    border: 'none',
                    marginLeft: 2
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0c000'}
                  onMouseOut={e => e.currentTarget.style.background = 'var(--primary-color)'}
                >
                  Add
                </button>
              </form>
              <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <h4 style={{ fontWeight: 500, fontSize: '1rem', marginBottom: 8, color: 'var(--text)' }}>Employee URLs</h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {employeeUrls.length === 0 && <li style={{ color: 'var(--text-light)' }}>None</li>}
                    {employeeUrls.map(url => (
                      <li key={url['id']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '4px 0' }}>
                        <div style={{ 
                          flex: 1, 
                          minWidth: 0, 
                          display: 'flex', 
                          alignItems: 'center',
                          overflow: 'hidden'
                        }}>
                          <span style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1,
                            minWidth: 0
                          }} title={url['name']}>
                            {url['name']}
                          </span>
                          <span style={{ 
                            fontSize: 12, 
                            color: 'var(--text-light)', 
                            marginLeft: 8,
                            flexShrink: 0
                          }} title="Frequency">
                            ({formatFrequency(url['frequency'], url['week_day'])})
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteUrl(url['name'])}
                          className="btn btn-link"
                          style={{ color: 'var(--error)', fontSize: 16, marginLeft: 8, flexShrink: 0 }}
                          title="Delete URL"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <h4 style={{ fontWeight: 500, fontSize: '1rem', marginBottom: 8, color: 'var(--text)' }}>User URLs</h4>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {userUrls.length === 0 && <li style={{ color: 'var(--text-light)' }}>None</li>}
                    {userUrls.map(url => (
                      <li key={url['id']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '4px 0' }}>
                        <div style={{ 
                          flex: 1, 
                          minWidth: 0, 
                          display: 'flex', 
                          alignItems: 'center',
                          overflow: 'hidden'
                        }}>
                          <span style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1,
                            minWidth: 0
                          }} title={url['name']}>
                            {url['name']}
                          </span>
                          <span style={{ 
                            fontSize: 12, 
                            color: 'var(--text-light)', 
                            marginLeft: 8,
                            flexShrink: 0
                          }} title="Frequency">
                            ({formatFrequency(url['frequency'], url['week_day'])})
                          </span>
                        </div>
                        <button
                          onClick={() => handleDeleteUrl(url['name'])}
                          className="btn btn-link"
                          style={{ color: 'var(--error)', fontSize: 16, marginLeft: 8, flexShrink: 0 }}
                          title="Delete URL"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24, marginTop: 24 }}>
              <h3 style={{ fontWeight: 600, fontSize: '1.2rem', color: 'var(--text)', marginBottom: 20 }}>Monitored Slugs</h3>
              {slugSuccess && <div className="success-message mb-2">{slugSuccess}</div>}
              {slugError && <div className="error-message mb-2">{slugError}</div>}
              <form onSubmit={handleAddSlug}
                style={{
                  display: 'flex',
                  gap: 6,
                  marginBottom: 14,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  background: 'none',
                  borderRadius: 6,
                  boxShadow: '0 1px 3px rgba(30,39,56,0.04)',
                  minHeight: 0
                }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Slug"
                  value={slugName}
                  onChange={e => setSlugName(e.target.value)}
                  style={{ flex: 3, minWidth: 80, fontSize: 13, padding: '3px 7px', height: 28, borderRadius: 4, border: '1px solid var(--border)', background: '#374151', color: 'var(--text)', marginRight: 2 }}
                  required
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{
                    flex: 1,
                    minWidth: 60,
                    fontSize: 13,
                    padding: '3px 0',
                    height: 28,
                    borderRadius: 4,
                    boxShadow: 'none',
                    fontWeight: 500,
                    background: 'var(--primary-color)',
                    color: 'var(--secondary-color)',
                    transition: 'background 0.18s',
                    border: 'none',
                    marginLeft: 2
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0c000'}
                  onMouseOut={e => e.currentTarget.style.background = 'var(--primary-color)'}
                >
                  Add
                </button>
              </form>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {slugs.length === 0 && <li style={{ color: 'var(--text-light)' }}>None</li>}
                {slugs.map(slug => (
                  <li key={slug['id']} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, padding: '4px 0' }}>
                    <span style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                      minWidth: 0
                    }} title={slug['name']}>
                      {slug['name']}
                    </span>
                    <button
                      onClick={() => handleDeleteSlug(slug['name'])}
                      className="btn btn-link"
                      style={{ color: 'var(--error)', fontSize: 16, marginLeft: 8, flexShrink: 0 }}
                      title="Delete Slug"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 24, marginTop: 24 }}>
              <h3 style={{ fontWeight: 600, fontSize: '1.2rem', color: 'var(--text)', marginBottom: 20 }}>Import from CSV</h3>
              {importError && <div className="error-message mb-2">{importError}</div>}
              {importResult && (
                <div className="success-message mb-2">
                  {importResult.created.length} added, {importResult.rejected.length} not imported
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="btn"
                  style={{ flex: 1, minWidth: 120, fontSize: 13, padding: '3px 0', height: 28, borderRadius: 4, fontWeight: 500, background: 'none', border: '1px solid var(--border)', color: 'var(--text)' }}
                >
                  <i className="fas fa-download" style={{ marginRight: 6 }}></i>
                  Download template
                </button>
                <label
                  htmlFor="importFile"
                  className="btn btn-primary"
                  style={{
                    flex: 1,
                    minWidth: 120,
                    fontSize: 13,
                    padding: '3px 0',
                    height: 28,
                    borderRadius: 4,
                    fontWeight: 500,
                    background: 'var(--primary-color)',
                    color: 'var(--secondary-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: importing ? 'wait' : 'pointer',
                    opacity: importing ? 0.6 : 1,
                    margin: 0
                  }}
                >
                  <i className="fas fa-file-import" style={{ marginRight: 6 }}></i>
                  {importing ? 'Importing...' : 'Import CSV'}
                </label>
                <input
                  id="importFile"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleImportFile}
                  disabled={importing}
                  hidden
                />
              </div>
              {importResult && (importResult.rejected.length > 0 || importResult.created.some(row => row.notes.length > 0)) && (
                <ul style={{ listStyle: 'none', padding: 0, fontSize: 13 }}>
                  {importResult.rejected.map(row => (
                    <li key={`rejected-${row.line}`} style={{ color: 'var(--error)', marginBottom: 4 }}>
                      Line {row.line}{row.name ? ` (${row.name})` : ''}: {row.reason}
                    </li>
                  ))}
                  {importResult.created.filter(row => row.notes.length > 0).map(row => (
                    <li key={`note-${row.line}`} style={{ color: 'var(--text-light)', marginBottom: 4 }}>
                      Line {row.line} ({row.name}): {row.notes.join('; ')}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminEmail; 